import moment from "moment";
import "moment-timezone";

/**
 * TRAD-1295 — Wealth Series rebalance basis disclosure.
 *
 * Wealth Series performance is restated from a market-open rebalance basis onto
 * the actual 10:00 AM ET basis the desk trades on. Every figure that reflects the
 * restated series has to carry this disclosure, its period, and its as-of date.
 *
 * Wording below is the compliance-approved version (signed off 2026-08-17). The
 * Aug 4/5 dates that appear in the ticket description and in both delta specs are
 * superseded by July 21/22 — the desk moved to 10:00 execution on the Jul 22, 2026
 * rebalance, and the Jul 22 and Aug 5 rebalances were re-priced to match. Do not
 * reintroduce the Aug dates.
 *
 * Scope is Wealth Series only (Atlas / Titan / Everest / Core). Wealth Series Lite
 * and Bitcoin Navigator sit on their own basis and must not render this copy.
 */

// Series slugs this disclosure applies to. Bitcoin Navigator shares the portfolio
// performance graph but is a separate product on its own basis, and Wealth Series
// Lite flips with its own launch — neither renders this copy.
export const WEALTH_SERIES_TIERS = ["atlas", "titan", "everest", "core"];

// Footnote-length version, for the disclosure block and the portfolio chart.
export const REBALANCE_BASIS_FULL =
  "Performance reflects a market-open rebalance for periods through July 21, 2026, " +
  "and a 10:00 AM ET rebalance thereafter, consistent with how model trades are " +
  "placed. Individual results vary with execution timing.";

// Compact version, for tooltips and inline notes next to a figure.
export const REBALANCE_BASIS_SHORT =
  "Rebalanced at 10:00 AM ET (market-open basis before July 22, 2026).";

/**
 * The AI Trade Price column is a separate claim from the performance series, and
 * it is not true yet (backend response, 2026-08-18).
 *
 * Those prices come from a stored column (`ai_rebalance_price`), not a snapshot
 * read, so the `dataset=v2` flip never touches them. The backend re-priced the
 * source going forward — first live run is the Aug 19, 2026 rebalance — but the
 * historical Jul 22 and Aug 5 rows keep their pre-market (~07:52 ET) stamps
 * until a data-side backfill lands. Measured deviation from the real 10:00
 * prints on those rows: ~1% median, 6% worst case.
 *
 * So the sentence that dates the prices themselves stays out of the footnote
 * until the backfill is confirmed. Flip this to `true` then — the wording below
 * is already the compliance-approved text, and nothing else has to change.
 */
export const AI_TRADE_PRICE_BACKFILL_COMPLETE = false;

// Portfolio Activity footnote. The AI Trade Price values *are* the timing
// assumption, so the boundary date is restated against the rows themselves —
// but only once those rows actually carry 10:00 prices.
export const REBALANCE_BASIS_ACTIVITY_FOOTNOTE =
  AI_TRADE_PRICE_BACKFILL_COMPLETE
    ? `${REBALANCE_BASIS_SHORT} AI Trade Prices for entries dated July 22, 2026 or ` +
      "later reflect 10:00 AM ET prices."
    : REBALANCE_BASIS_SHORT;

/**
 * As-of date for a Wealth Series figure, formatted "Aug 17, 2026".
 *
 * Always derived from the API's `last_updated` — never the system clock, so the
 * date can never drift ahead of the data it labels. Returns null when the API has
 * not supplied one yet, and every caller drops the date rather than dating a
 * figure it cannot vouch for.
 */
export const formatAsOfDate = (lastUpdated) => {
  if (!lastUpdated) return null;
  const parsed = moment(lastUpdated);
  if (!parsed.isValid()) return null;
  return parsed.tz("America/New_York").format("MMM D, YYYY");
};

/**
 * Portfolio tile label. Replaces the old "Annual P/L*", which left the period
 * undefined and differed from the homepage's YTD basis without saying so.
 */
export const trailingPerformanceLabel = (asOf) =>
  asOf ? `Trailing 12-mo P/L as of ${asOf}` : "Trailing 12-mo P/L";

/**
 * The canonical disclosure block rendered once per Wealth Series overview, below
 * the portfolio tiles. Same text as the homepage footnote, plus the basis
 * disclosure — one block per view rather than a short note on every tile.
 */
export const wsPerformanceDisclosure = (asOf) => {
  const period = asOf
    ? `on a trailing 12-month basis (as of ${asOf})`
    : "on a trailing 12-month basis";
  return (
    "Performance reflects AI-generated representative portfolios, shown " +
    `${period} gross of fees, transaction costs, and slippage. Actual results ` +
    "may differ materially. Past performance is not indicative of future " +
    `results. ${REBALANCE_BASIS_FULL}`
  );
};
